import { combineReducers, type Action, type Reducer } from '@reduxjs/toolkit';
import uiReducer from '@/features/ui/uiSlice';
import userReducer, { logoutUser } from '@/features/user/userSlice';
import accountModalReducer from '@/features/user/accountModalSlice';
import cartReducer from '@/features/cart/cartSlice';
import checkoutReducer from '@/features/checkout/checkoutSlice';
import reservationReducer from '@/features/reservation/reservationSlice';
import tableSessionReducer from '@/features/tableSession/tableSessionSlice';
import waiterReducer from '@/features/waiter/waiterSlice';
import waiterCartReducer from '@/features/waiter/waiterCartSlice';
import { hubApi } from './api/hubApi';
import { resourceApi } from './api/resourceApi';
import { saleApi } from './api/saleApi';
import { loyaltyApi } from '@/features/loyalty/loyaltyApi';

// The single list of slices the app runs on. It lives apart from store.ts so
// tests can mount the SAME map instead of re-declaring their own: a test store
// missing a slice a shared component reads renders `undefined` where state is
// expected and the component throws before the first assertion. That is exactly
// how the three integration suites went red when RestaurantShell started
// reading `tableSession` (SC-579) — and how CheckoutPage broke before them.
export const rootReducer = combineReducers({
  ui: uiReducer,
  user: userReducer,
  accountModal: accountModalReducer,
  cart: cartReducer,
  checkout: checkoutReducer,
  reservation: reservationReducer,
  // tableSession is NOT in redux-persist whitelist (persisted via sessionStorage
  // directly in the slice). It survives logoutUser via sessionStorage rehydrate.
  tableSession: tableSessionReducer,
  // waiter follows the same pattern as tableSession, but via localStorage
  // (device-scoped, survives tab close) — see waiterSlice.ts.
  waiter: waiterReducer,
  // waiterCart follows the tableSession pattern too, but keyed by sessionUuid
  // (one ticket = one cart) and via sessionStorage — see waiterCartSlice.ts.
  // Deliberately NOT in store.ts's redux-persist whitelist (['cart', 'user']).
  waiterCart: waiterCartReducer,
  [hubApi.reducerPath]: hubApi.reducer,
  [resourceApi.reducerPath]: resourceApi.reducer,
  [saleApi.reducerPath]: saleApi.reducer,
  [loyaltyApi.reducerPath]: loyaltyApi.reducer,
});

// Every RTK Query middleware the reducer map above needs. Registering a query
// slice without its middleware makes the hook warn and the render abort, so the
// two lists must stay together.
export const apiMiddleware = [
  hubApi.middleware,
  resourceApi.middleware,
  saleApi.middleware,
  loyaltyApi.middleware,
] as const;

// The reducer paths whose state must NOT be replaced wholesale on logout.
// Resetting an RTK Query slice to its initialState wipes config.middlewareRegistered
// back to false and drops the live subscriptions map — every mounted useQuery hook
// then stops receiving updates and the page goes blank. Caches are cleared via
// `api.util.resetApiState()` from the store's logout listener instead, which
// preserves middleware registration and lets active subscribers refetch cleanly.
const API_REDUCER_PATHS = [
  hubApi.reducerPath,
  resourceApi.reducerPath,
  saleApi.reducerPath,
  loyaltyApi.reducerPath,
] as const;

// Wraps a reducer so a logoutUser dispatch resets every consumer slice to its
// initialState (cart, checkout, addresses, reservation, account modal, ui, user),
// while leaving the RTK Query slices untouched. Same browser, different consumer →
// clean slate for consumer state; RTK Query caches are wiped separately by the
// listener middleware so subscriptions stay alive.
export function withLogoutReset<S, A extends Action>(inner: Reducer<S, A>): Reducer<S, A> {
  return (state, action) => {
    if (action.type === logoutUser.type && state) {
      const fresh = inner(undefined, action) as Record<string, unknown>;
      const prev = state as Record<string, unknown>;
      const next = { ...fresh };
      for (const path of API_REDUCER_PATHS) next[path] = prev[path];
      return next as S;
    }
    return inner(state, action);
  };
}
